import { useEffect, useCallback, useRef, useState, RefObject } from "react";
import {
  KeyboardNavigationManager,
  KeyboardNavigationKeys,
} from "../lib/KeyboardNavigationManager";

export type { KeyboardNavigationKeys };

export interface UseKeyboardNavigationOptions {
  itemCount: number;
  initialIndex?: number;
  direction?: "vertical" | "horizontal";
  onIndexChange?: (index: number) => void;
  enabled?: boolean;
  containerRef?: RefObject<HTMLElement | null>; // Optional container ref for scoped keyboard events
  targetElement?: HTMLElement | Window | null; // Optional explicit target element (takes precedence over containerRef)
  keys?: KeyboardNavigationKeys; // Optional custom key mappings
}

export function useKeyboardNavigation({
  itemCount,
  initialIndex = 0,
  direction = "horizontal",
  onIndexChange,
  enabled = true,
  containerRef,
  targetElement,
  keys,
}: UseKeyboardNavigationOptions) {
  // Store the manager instance in a ref to avoid recreating it
  const managerRef = useRef<KeyboardNavigationManager | null>(null);

  // Only re-render when currentIndex changes
  const [currentIndex, setCurrentIndex] = useState(() => initialIndex);

  // Initialize manager only once
  useEffect(() => {
    // Determine target element - targetElement explicitly set takes precedence, then containerRef, then window
    const resolvedTargetElement =
      targetElement !== undefined
        ? targetElement
        : containerRef?.current || window;

    if (!managerRef.current) {
      managerRef.current = new KeyboardNavigationManager({
        itemCount,
        initialIndex,
        direction,
        onIndexChange,
        enabled,
        targetElement: resolvedTargetElement,
        keys,
      });

      // Subscribe to changes
      managerRef.current.subscribe((newIndex) => {
        setCurrentIndex(newIndex);
      });

      // Set initial values
      setCurrentIndex(managerRef.current.getCurrentIndex());
    }

    return () => {
      managerRef.current?.destroy();
      managerRef.current = null;
    };
  }, []); // Only run once on mount

  // Update manager when options change
  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.updateItemCount(itemCount);
    }
  }, [itemCount]);

  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.updateEnabled(enabled);
    }
  }, [enabled]);

  useEffect(() => {
    if (managerRef.current) {
      // Determine target element
      const resolvedTargetElement =
        targetElement !== undefined
          ? targetElement
          : containerRef?.current || window;
      managerRef.current.updateTargetElement(resolvedTargetElement);
    }
  }, [targetElement, containerRef?.current]);

  useEffect(() => {
    if (managerRef.current && keys) {
      managerRef.current.updateKeys(keys);
    }
  }, [keys]);

  // Update onIndexChange when it changes
  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.updateOnIndexChange(onIndexChange);
    }
  }, [onIndexChange]);

  // Memoized callbacks that use the manager
  const navigateToIndex = useCallback((newIndex: number) => {
    return managerRef.current?.navigateToIndex(newIndex) ?? false;
  }, []);

  const navigateByOffset = useCallback((offset: number) => {
    return managerRef.current?.navigateByOffset(offset) ?? false;
  }, []);

  return {
    currentIndex,
    navigateToIndex,
    navigateByOffset,
  };
}
